import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, InputNumber, Row, Col, Divider, Space, Button, Spin, message, Alert, Select, Switch, Tooltip } from 'antd';
import { SaveOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import API from '../../../API';

const { TextArea } = Input;

/**
 * ScopedSummarySettings
 * A reusable summary-settings form for any scope: project, procuring entity, package, sub-project.
 * Props:
 * - scopeType: 'project' | 'package' | 'sub_project' | 'project_region' | 'project_council'
 * - scopeId: string | number | null
 * - canSave: boolean (enables/disables saving; use true for project only until backend endpoints exist for others)
 */
const ScopedSummarySettings = ({ scopeType = 'project', scopeId, canSave = true, customEndpoint, onClose   }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const title = useMemo(() => {
  switch (scopeType) {
      case 'package':
        return 'Package Summary Settings';
      case 'sub_project':
        return 'Sub-Project Summary Settings';
      case 'project_region':
        return 'Region Summary Settings';
      case 'project_council':
        return 'Council Summary Settings';
      default:
        return 'Project Summary Settings';
    }
  }, [scopeType]);

  const endpoint = useMemo(() => {
    if (customEndpoint) return customEndpoint;
    if (!scopeId) return null;
  switch (scopeType) {
      case 'package':
        return `/procuring_entity_packages/${scopeId}/summary-data`;
      case 'sub_project':
        return `/sub_projects/${scopeId}/summary-data`;
      case 'project_region':
        return `/projects/${scopeId.projectId}/regions/${scopeId.regionId}/summary-data`;
      case 'project_council':
        return `/projects/${scopeId.projectId}/councils/${scopeId.councilId}/summary-data`;
      case 'project':
      default:
        return `/projects/${scopeId}/summary-data`;
    }
  }, [scopeType, scopeId, customEndpoint]);

  const fetchSummaryData = async () => {
    if (!endpoint) return;
    setLoading(true);
    try {
      const payload = await API.get(endpoint);
      const summaryData = payload?.summary_data || {};
      form.setFieldsValue(summaryData);
    } catch (e) {
      // No existing data or endpoint not available yet
      form.resetFields();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    form.resetFields();
    fetchSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const handleReset = () => {
    form.resetFields();
    fetchSummaryData();
  };

  const handleSave = async (values) => {
    if (!endpoint) {
      message.info('Select an item first.');
      return;
    }
    if (!canSave) {
      message.info('Saving for this level will be enabled once backend endpoints are available.');
      return;
    }
    setSaving(true);
    try {
      await API.post(endpoint, { summary_data: values });
      message.success('Summary data saved');
      fetchSummaryData();
      // Only close modal on successful save
      if (onClose) {
        onClose();
      }
    } catch (e) {
      message.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={title}
      style={{ marginTop: 16 }}
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset} disabled={!scopeId || loading}>
            Reset
          </Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={() => form.submit()} loading={saving} disabled={!scopeId || !canSave}>
            Save Changes
          </Button>
        </Space>
      }
    >
      {!scopeId && (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="Select an item to configure its summary settings."
          style={{ marginBottom: 12 }}
        />
      )}
      {!canSave && scopeId && (
        <Alert
          type="warning"
          showIcon
          message="Saving disabled"
          description="Entity-level saving will be enabled once backend endpoints are implemented."
          style={{ marginBottom: 12 }}
        />
      )}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSave} disabled={!scopeId || loading}>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label="Headline" name="headline" help="Main headline text displayed in the summary">
                <TextArea rows={2} placeholder="e.g., Summary headline for this scope" />
              </Form.Item>
            </Col>
          </Row>

          {/* Removed fixed Key Figures (Total Funding, Duration) — use Custom Statistics instead */}

          <Divider>Custom Statistics</Divider>

          <Form.List name="custom_items">
            {(fields, { add, remove, move }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Card key={key} size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                    <Row gutter={12}>
                      <Col xs={24} md={8}>
                        <Form.Item {...restField} name={[name, 'label']} label="Label" rules={[{ required: true, message: 'Missing label' }]}>
                          <Input placeholder="e.g., Bus Terminals" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item {...restField} name={[name, 'value']} label="Value" rules={[{ required: true, message: 'Missing value' }]}>
                          <InputNumber style={{ width: '100%' }} placeholder="e.g., 42" />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Item {...restField} name={[name, 'prefix']} label="Prefix">
                          <Input placeholder="$" />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Item {...restField} name={[name, 'suffix']} label="Suffix">
                          <Input placeholder="%" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} name={[name, 'format']} label="Format" initialValue="raw">
                          <Select
                            options={[
                              { value: 'raw', label: 'Raw' },
                              { value: 'comma', label: '1,234' },
                              { value: 'compact', label: '1.2K' },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} name={[name, 'type']} label="Style" initialValue="metric">
                          <Select
                            options={[
                              { value: 'metric', label: 'Metric' },
                              { value: 'pill', label: 'Pill' },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} name={[name, 'width']} label="Width" tooltip="Columns per row" initialValue={3}>
                          <Select
                            options={[
                              { value: 1, label: 'Full (1/1)' },
                              { value: 2, label: 'Half (1/2)' },
                              { value: 3, label: 'Third (1/3)' },
                              { value: 4, label: 'Quarter (1/4)' },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} name={[name, 'visible']} label="Visible" valuePropName="checked" initialValue={true}>
                          <Switch />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} name={[name, 'color']} label="Text Color">
                          {/* Use a native color input for broad compatibility */}
                          <input type="color" style={{ height: 32, width: 48, padding: 0, border: '1px solid #d9d9d9', borderRadius: 4 }} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item {...restField} name={[name, 'bg']} label="Background">
                          <input type="color" style={{ height: 32, width: 48, padding: 0, border: '1px solid #d9d9d9', borderRadius: 4 }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 30 }}>
                          <Tooltip title="Move up">
                            <Button disabled={index === 0} onClick={() => move(index, index - 1)}>↑</Button>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <Button disabled={index === fields.length - 1} onClick={() => move(index, index + 1)}>↓</Button>
                          </Tooltip>
                          <Tooltip title="Duplicate">
                            <Button
                              onClick={() => {
                                const items = form.getFieldValue('custom_items') || [];
                                const clone = { ...(items[index] || {}) };
                                // reset nothing; keep values for quick edits
                                const insertAt = index + 1;
                                // Form.List add supports (defaultValue, insertIndex)
                                add(clone, insertAt);
                              }}
                            >
                              Duplicate
                            </Button>
                          </Tooltip>
                          <Button danger onClick={() => remove(name)}>Remove</Button>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add({ type: 'metric', width: 3, visible: true })} style={{ width: '100%' }}>
                    Add Custom Statistic
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      )}
    </Card>
  );
};

export default ScopedSummarySettings;
